<template lang='pug'>
    h1.has-text-centered(:title="display.title", tabindex="0") {{ display.horario }}
</template>

<script>
import moment from 'moment'
import { converterHorarioUtcParaComTimezone } from '@/utils'
import { tratarErroDeGeolocalizacao } from '../components/Marcacao/utils'
import { gerarUrlHorarioLocalizacao, gerarUrlHorarioTimeZone } from '../api'

export default {
  async created () {
    this.getFallBackTime()
    await this.buscarGeolocalizacao()
    this.$nextTick(function () {
      this.getTime()
    })
  },
  data () {
    return {
      time: null,
      fallBackTime: null,
      fallBackWorker: null,
      worker: null,
      latitude: null,
      longitude: null,
      geoError: null,
      horarioGeolocalizacaoCarregado: false,
      horarioDoLocalCarregado: false,
      currentTimeTimezone: null,
      fallBackTimezone: null
    }
  },
  computed: {
    display () {
      const textoFallBack = `${this.$t('comum.currentTime.textoFallBack1')} ${
        this.fallBackTimezone
      }, ${this.$t('comum.currentTime.textoFallBack2')}`
      const { localDeTrabalhoUltimoPonto } = this.$store.state.accessData

      const textoPadraoCurrentTime = `${this.$t('comum.currentTime.textoPadraoCurrentTime')} ${this.currentTimeTimezone}, `
      const textoLocalTrabalho = `${this.$t('comum.currentTime.textoLocalTrabalho')} ${localDeTrabalhoUltimoPonto}`
      const textoGeolocalizacao = `${this.$t('comum.currentTime.textoGeolocalizacao')} Lat: ${this.latitude} e Long: ${this.longitude}`

      if (this.horarioDoLocalCarregado) return { horario: this.time, title: textoPadraoCurrentTime + textoLocalTrabalho }
      if (this.horarioGeolocalizacaoCarregado) return { horario: this.time, title: textoPadraoCurrentTime + textoGeolocalizacao }

      return { horario: this.fallBackTime, title: textoFallBack }
    }
  },
  methods: {
    async getFallBackTime ()  {
      const {origin, pathname} = window.location
      const urlDoWorker = `${origin}${pathname}js/fallbackCurrentTimeWorker.js`

      let date = new Date()
      const timezone =  Intl.DateTimeFormat(date).resolvedOptions().timeZone
      this.fallBackTimezone = timezone
      this.fallBackWorker = new Worker(urlDoWorker)

      this.fallBackWorker.postMessage(date.toISOString())

      this.fallBackWorker.addEventListener('message', (e) => {
        this.fallBackTime = e.data
      })
    },
    async getTime () {
      const { empregadoId, localId } = this.$store.state.marcacao
      const { timeZoneLocalDeTrabalho } = this.$store.state.accessData

      this.$nextTick(() => {
        this.updateTimeWorker(empregadoId, localId, timeZoneLocalDeTrabalho)
      })
    },
    async updateTimeWorker (empregadoId, localId, timeZoneLocalDeTrabalho) {
      /*
        Quando deployado em dominios dentro de uma estrutura de pastas, ex: hml.pontotel.com.br/alpha/new-web, é preciso garantir o caminho correto usando o pathname.
        A estrutura da url foi montada para que o arquivo seja localizado na pasta pública relativa correta.
      */
      /*
        Quando estiver em /marcacao, é retornado o horario validado pelo REPP ou da Geolocalização, caso o local de trabalho aceitar o timezone do coletor;
        Quando estiver na rota de sincronizacao, verifica-se no localStorage se existe o timezone da ultima marcação, retornando o horario de acordo com a timezone;
        Caso não tenha o timezone da ultima marcação, tenta-se retornar o horario da geolocalização;
        Em ultima instancia, retorna o horario do computador
     */
      const {origin, pathname} = window.location
      const urlDoWorker = `${origin}${pathname}js/currentTimeWorker.js`

      let date

      this.worker = new Worker(urlDoWorker)

      date = await this.atribuirHorarioRelogio(empregadoId, localId, timeZoneLocalDeTrabalho)

      this.worker.postMessage(date.toISOString())
      this.worker.addEventListener('message', (e) => {
        this.time = e.data
      })
    },
    async atribuirHorarioRelogio (empregadoId, localId, timeZoneLocalDeTrabalho) {
      const rotaAtual = this.$route.name
      const estaValidadoNaRotaDeMarcacao = empregadoId && localId && rotaAtual ===  'marcacao'
      const possuiDadosDaGeolocalizacao = this.latitude && this.longitude
      const erroDeGeolocalizacao = this.$store.state.marcacao.erroDeGeolocalizacao
      const localDeTrabalhoMarcacao = this.$store.state.marcacao.localDeTrabalhoMarcacao

      if (
        estaValidadoNaRotaDeMarcacao &&
        localDeTrabalhoMarcacao &&
        (!localDeTrabalhoMarcacao.aceita_timezone_do_coletor ||
        erroDeGeolocalizacao)
      ) {
        return this.buscarHorarioValidacaoPreMarcacao(empregadoId, localId)
      }
      if (
        estaValidadoNaRotaDeMarcacao &&
        possuiDadosDaGeolocalizacao &&
        localDeTrabalhoMarcacao &&
        localDeTrabalhoMarcacao.aceita_timezone_do_coletor
      ) {
        await this.$store.dispatch('marcacao/validarPreMarcacao')
        return this.buscarHorarioDaGeolocalizacao()
      }
      if (rotaAtual === 'sincronizador' && timeZoneLocalDeTrabalho) {
        return this.buscarHorarioDaTimezone(timeZoneLocalDeTrabalho)
      }
      if (
        possuiDadosDaGeolocalizacao &&
        rotaAtual ===  'sincronizador' &&
        !this.horarioDoLocalCarregado
      ) {
        return this.buscarHorarioDaGeolocalizacao()
      }
      return new Date()
    },
    async buscarHorarioValidacaoPreMarcacao () {
      const res = await this.$store.dispatch('marcacao/validarPreMarcacao')
      const { horarioUtc, timezone } = res.body

      const dataFormatada = converterHorarioUtcParaComTimezone(horarioUtc, timezone)

      this.$store.commit('marcacao/atribuiTimeZoneLocalDeTrabalho', timezone)
      this.currentTimeTimezone = timezone
      this.horarioDoLocalCarregado = true

      return moment(dataFormatada).toDate()
    },
    async buscarHorarioDaTimezone (timeZoneLocalDeTrabalho) {
      const timeZone = encodeURI(encodeURIComponent(timeZoneLocalDeTrabalho))
      try {
        const res = await this.$http.get(gerarUrlHorarioTimeZone(timeZone))
        const { horarioLocal, timezone } = res.body
        const data = converterHorarioUtcParaComTimezone(horarioLocal, timezone)

        this.currentTimeTimezone = timezone
        this.horarioDoLocalCarregado = true

        return moment(data).toDate()
      } catch (e) {
        return new Date()
      }
    },
    async buscarHorarioDaGeolocalizacao () {
      this.$store.commit('marcacao/atribuiHorarioDaGeolocalizacaoCarregado', false)
      try {
        const res = await this.$http.get(gerarUrlHorarioLocalizacao(this.latitude, this.longitude))
        const { horarioLocal, timezone } = res.body

        const data = converterHorarioUtcParaComTimezone(horarioLocal, timezone)

        this.$store.commit('marcacao/atribuiHorarioDaGeolocalizacaoCarregado', true)
        this.$store.commit('marcacao/atribuiTimeZoneDaGeolocalizacao', timezone, {root: true})

        this.currentTimeTimezone = timezone
        this.horarioGeolocalizacaoCarregado = true

        return moment(data).toDate()
      } catch (e) {
        this.$store.commit('marcacao/atribuiErroDeGeolocalizacao', 'ERRO_AO_CONSULTAR_REPP', {root: true})
        return new Date()
      }
    },
    getCoordinates () {
      const options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
      return new Promise(function (resolve, reject) {
        navigator.geolocation.getCurrentPosition(resolve, reject, options)
      })
    },
    async buscarGeolocalizacao () {
      if (navigator.geolocation) {
        try {
          const { coords } = await this.getCoordinates()
          this.latitude = coords.latitude
          this.longitude = coords.longitude
          console.warn('Latitude:', this.latitude, ' Longitude:', this.longitude)
          this.$store.commit('marcacao/atribuiInformacoesGeolocalizacao', coords, {root: true})
        } catch (e) {
          this.$store.commit('marcacao/atribuiErroDeGeolocalizacao', tratarErroDeGeolocalizacao(e.code), {root: true})
          this.geoError = true
        }
      } else {
        console.warn('Navegador não possui suporte a geolocalização')
        this.$store.commit('marcacao/atribuiErroDeGeolocalizacao', 'NAVEGADOR_SEM_SUPORTE', {root: true})
      }
    }
  },
  watch: {
    time (currentValue) {
      this.$store.commit('marcacao/atribuiHorarioColetor', currentValue)
    }
  }
}
</script>
